import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './AnalysisChat.css';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
}

interface AnalysisChatProps {
  userProjects: any[];
  availableProjects: any[];
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  onBack: () => void;
  currentTheme?: string;
}

const AnalysisChat: React.FC<AnalysisChatProps> = ({
  userProjects,
  availableProjects,
  authenticatedFetch,
  onBack,
  currentTheme = 'modern'
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: '你好！我是你的智能项目管理助手 🤖\n\n我可以帮你：\n• 📊 分析项目进度和效率\n• 📈 提供时间管理建议\n• 🎯 制定项目目标和计划\n• 📋 生成项目报告\n• 💡 优化工作流程\n\n请告诉我你想了解什么，或者直接问我"分析我的项目"来开始！',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // 左滑回到首页相关状态
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 左滑回到首页事件处理
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
    setSwipeX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - touchStartX.current;
    const deltaY = Math.abs(currentY - touchStartY.current);
    
    // 确保是水平滑动（垂直滑动距离小于水平滑动距离的一半）
    if (deltaY < Math.abs(deltaX) * 0.5) {
      // 只允许向右滑动（左滑回到首页）
      if (deltaX > 0) {
        const maxSwipe = 100; // 最大滑动距离
        const swipeDistance = Math.min(deltaX, maxSwipe);
        setSwipeX(swipeDistance);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    // 如果滑动距离超过50px，触发回到首页
    if (swipeX > 50) {
      onBack();
    } else {
      // 否则重置滑动状态
      setSwipeX(0);
    }
    
    setIsSwiping(false);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // 准备项目数据供AI分析
      const projectData = userProjects.map(userProject => {
        const projectInfo = availableProjects.find(p => p.id === userProject.id);
        return {
          ...userProject,
          projectInfo
        };
      }).filter(p => p.projectInfo);

      const response = await authenticatedFetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          projectData: projectData,
          availableProjects: availableProjects
        })
      });

      if (!response.ok) {
        throw new Error('AI服务暂时不可用');
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI聊天错误:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '抱歉，AI服务暂时不可用。请稍后再试，或者你可以尝试重新表述你的问题。',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (action: string) => {
    setInputValue(action);
    setTimeout(() => handleSendMessage(), 100);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('zh-CN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const renderMessageContent = (content: string) => {
    // 检查内容是否包含Markdown格式（如**、##、-等）
    const hasMarkdown = /[*#\-`\[\]]/.test(content);
    
    if (hasMarkdown) {
      return (
        <ReactMarkdown
          className="markdown-content"
          remarkPlugins={[]}
          rehypePlugins={[]}
        >
          {content}
        </ReactMarkdown>
      );
    } else {
      // 对于纯文本，保持原有的换行处理
      return content.split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < content.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
    }
  };

  return (
    <div 
      className={`chat-container theme-${currentTheme}`}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateX(${swipeX}px)`,
        transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* 左滑提示遮罩 */}
      {swipeX > 20 && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(102, 126, 234, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <div 
            style={{
              background: 'rgba(102, 126, 234, 0.9)',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '500',
              transform: `translateX(${-swipeX * 0.5}px)`,
              opacity: Math.min(swipeX / 50, 1)
            }}
          >
            左滑回到首页
          </div>
        </div>
      )}
      
      {/* 消息列表 */}
      <div className="messages-wrapper">
        <div className="messages-list">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message-item ${message.type}`}
            >
              {message.type === 'assistant' && (
                <div className="avatar">
                  <div className="avatar-icon">AI</div>
                </div>
              )}
              
              {message.type === 'user' && (
                <div className="avatar user-avatar">
                  <div className="avatar-icon">我</div>
                </div>
              )}
              
              <div className="message-bubble">
                <div className="message-content">
                  {renderMessageContent(message.content)}
                </div>
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message-item assistant">
              <div className="avatar">
                <div className="avatar-icon">AI</div>
              </div>
              <div className="message-bubble">
                <div className="message-content">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="quick-actions">
        <button 
          className="quick-btn"
          onClick={() => handleQuickAction('分析我的项目进度')}
        >
          📊 项目进度
        </button>
        <button 
          className="quick-btn"
          onClick={() => handleQuickAction('给我一些时间管理建议')}
        >
          ⏰ 时间建议
        </button>
        <button 
          className="quick-btn"
          onClick={() => handleQuickAction('生成本周项目报告')}
        >
          📋 生成报告
        </button>
        <button 
          className="quick-btn"
          onClick={() => handleQuickAction('帮我优化工作流程')}
        >
          🔧 优化流程
        </button>
      </div>

      {/* 输入区域 */}
      <div className="input-area">
        <div className="input-wrapper">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="输入消息..."
            className="message-input"
            rows={1}
            disabled={isLoading}
          />
          <button 
            className="send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisChat; 